import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, ArrowLeft, Flame, Calendar, PenLine, Mic, 
  Quote, Check, ChevronRight, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  identity: "bg-purple-100 text-purple-700",
  gratitude: "bg-amber-100 text-amber-700",
  service: "bg-emerald-100 text-emerald-700",
  courage: "bg-red-100 text-red-700",
  ethics: "bg-blue-100 text-blue-700",
  self_love: "bg-rose-100 text-rose-700",
  accountability: "bg-orange-100 text-orange-700"
};

export default function CharacterStudio() {
  const { user } = useAuth();
  
  const [todaysPrompt, setTodaysPrompt] = useState(null);
  const [reflection, setReflection] = useState('');
  const [history, setHistory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('reflect');

  useEffect(() => {
    fetchTodaysPrompt();
    if (user) fetchHistory();
  }, [user]);

  const fetchTodaysPrompt = async () => {
    try {
      const res = await fetch(`${API}/api/foundation/reflection/today`);
      if (res.ok) {
        setTodaysPrompt(await res.json());
      }
    } catch (err) {
      console.error('Error fetching prompt:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/foundation/reflection/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setHistory(await res.json());
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  const submitReflection = async () => {
    if (!reflection.trim()) {
      toast.error('Please write your reflection');
      return;
    }
    if (!user) {
      toast.error('Please log in to save reflections');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/foundation/reflection/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: reflection,
          category: todaysPrompt?.category || 'general'
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
        setReflection('');
        fetchHistory();
      }
    } catch (err) {
      toast.error('Failed to save reflection');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-rose-50/30" data-testid="character-studio">
      {/* Header */}
      <div className="bg-gradient-to-br from-rose-600 via-pink-600 to-rose-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/foundation" className="inline-flex items-center text-rose-200 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Foundation
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Heart className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Character Studio</h1>
          </div>
          <p className="text-rose-100">Build your character through daily reflection, one prompt at a time.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Streak Banner */}
        {history && (
          <Card className="mb-8 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-100 rounded-xl">
                    <Flame className="h-8 w-8 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-stone-800">
                      {history.streak}-Day Reflection Streak
                    </h3>
                    <p className="text-stone-600">
                      {history.streak === 0 ? "Start your streak today!" :
                       history.streak < 7 ? "Keep going! 7 days is your next milestone." :
                       history.streak < 30 ? "Impressive! 30 days earns the Consistent Self badge." :
                       "You're a reflection master!"}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-amber-600">{history.total_reflections}</div>
                  <p className="text-sm text-stone-500">Total Reflections</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button 
            variant={activeTab === 'reflect' ? 'default' : 'outline'}
            onClick={() => setActiveTab('reflect')}
            className={activeTab === 'reflect' ? 'bg-rose-600 hover:bg-rose-700' : ''}
          >
            <PenLine className="h-4 w-4 mr-2" /> Daily Reflection
          </Button>
          <Button 
            variant={activeTab === 'history' ? 'default' : 'outline'}
            onClick={() => setActiveTab('history')}
            className={activeTab === 'history' ? 'bg-rose-600 hover:bg-rose-700' : ''}
          >
            <Calendar className="h-4 w-4 mr-2" /> History
          </Button>
        </div>

        {activeTab === 'reflect' ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Today's Prompt */}
            <div className="lg:col-span-2">
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Quote className="h-5 w-5 text-rose-500" />
                      Today's Reflection
                    </CardTitle>
                    {todaysPrompt && (
                      <Badge className={categoryColors[todaysPrompt.category] || "bg-gray-100"}>
                        {todaysPrompt.category?.replace('_', ' ')}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-rose-50 to-pink-50 p-6 rounded-xl mb-6"
                  >
                    <p className="text-xl text-stone-800 font-medium leading-relaxed">
                      "{todaysPrompt?.prompt || "Loading today's prompt..."}"
                    </p>
                  </motion.div>

                  <div className="space-y-4">
                    <Textarea
                      placeholder={user ? "Write your reflection here... Let your thoughts flow freely." : "Log in to save your reflections"}
                      value={reflection}
                      onChange={(e) => setReflection(e.target.value)}
                      className="min-h-[200px] resize-none"
                      disabled={!user}
                    />
                    
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-stone-500">
                        {reflection.length} characters
                      </p>
                      <Button 
                        onClick={submitReflection}
                        disabled={submitting || !user || !reflection.trim()}
                        className="bg-rose-600 hover:bg-rose-700"
                      >
                        {submitting ? (
                          "Saving..."
                        ) : (
                          <><Check className="h-4 w-4 mr-2" /> Save Reflection</>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {!user && (
                <Card className="bg-rose-50 border-rose-200">
                  <CardContent className="py-6 text-center">
                    <Heart className="h-10 w-10 text-rose-400 mx-auto mb-3" />
                    <p className="text-stone-600 mb-4">Log in to save your reflections and build your streak.</p>
                    <Link to="/login">
                      <Button className="bg-rose-600 hover:bg-rose-700">Log In to Continue</Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Reflection Categories</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.entries(categoryColors).map(([cat, color]) => (
                    <div key={cat} className={`px-3 py-2 rounded-lg ${color} flex items-center justify-between`}>
                      <span className="capitalize text-sm font-medium">{cat.replace('_', ' ')}</span>
                      <ChevronRight className="h-4 w-4 opacity-50" />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Badges */}
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="py-6 text-center">
                  <Sparkles className="h-10 w-10 text-amber-500 mx-auto mb-2" />
                  <p className="font-medium text-stone-800">30-Day Streak</p>
                  <p className="text-sm text-stone-600">Earns the "Consistent Self" badge</p>
                </CardContent>
              </Card>

              {/* Self-Love Curriculum CTA */}
              <Card className="bg-gradient-to-br from-rose-600 to-pink-600 text-white">
                <CardContent className="py-6">
                  <h3 className="font-bold mb-2">Self-Love: An 8-Week Practice</h3>
                  <p className="text-rose-100 text-sm mb-4">
                    A structured journey through identity, worth, boundaries, rest, community, purpose, grief, and joy.
                  </p>
                  <Link to="/foundation/school/FC004">
                    <Button className="w-full bg-white text-rose-600 hover:bg-rose-50">
                      Start the Course
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* History Tab */
          <div className="space-y-4">
            {history?.reflections?.length > 0 ? (
              history.reflections.map((ref, idx) => (
                <motion.div
                  key={ref.date}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Card>
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-stone-400" />
                          <span className="text-sm text-stone-500">{ref.date}</span>
                        </div>
                        <Badge className={categoryColors[ref.category] || "bg-gray-100"}>
                          {ref.category?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-stone-700 whitespace-pre-wrap">{ref.content}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            ) : (
              <Card className="bg-stone-50">
                <CardContent className="py-12 text-center">
                  <PenLine className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">No reflections yet. Start your journey today!</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
